// ─── Constants ───────────────────────────────────────────────────────────────

const CHUNK_SIZE = 50000; // characters per chunk

// ─── Helper: split text into chunks ─────────────────────────────────────────

/**
 * Splits a long string into an array of chunks, each at most `chunkSize` chars.
 * Tries to split on the nearest newline or space before the limit
 * so words/sentences aren't cut in half.
 */
const splitTextIntoChunks = (text, chunkSize = CHUNK_SIZE) => {
  const chunks = [];
  let start = 0;

  while (start < text.length) {
    let end = start + chunkSize;

    if (end >= text.length) {
      // Last chunk — take whatever is left
      chunks.push(text.slice(start));
      break;
    }

    // Try to break on a newline first, then a space, to avoid mid-word cuts
    const newlineIdx = text.lastIndexOf('\n', end);
    const spaceIdx   = text.lastIndexOf(' ',  end);
    const breakIdx   = newlineIdx > start ? newlineIdx : spaceIdx > start ? spaceIdx : end;

    chunks.push(text.slice(start, breakIdx));
    start = breakIdx + 1; // +1 to skip the newline/space itself
  }

  return chunks;
};

// ─── Helper: create .txt and auto-download ───────────────────────────────────
 
/**
 * Creates a .txt file from `text` and triggers an automatic browser download.
 * The downloaded filename is the original file name with "_translated" appended,
 * e.g.  "report.pdf"  →  "report_translated.txt"
 *
 * @param {string} text         - Full translated text
 * @param {string} originalName - Original file name (e.g. "report.pdf")
 */
export const downloadAsTextFile = (text, originalName) => {
  // Build download filename:  "report.pdf" → "report_translated.txt"
  const baseName      = originalName.replace(/\.[^/.]+$/, ''); // strip extension
  const downloadName  = `${baseName}_translated.txt`;
 
  // Create a Blob and a temporary object URL
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
 
  // Create a hidden <a> tag, click it, then clean up
  const anchor    = document.createElement('a');
  anchor.href     = url;
  anchor.download = downloadName;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
 
  // Release the object URL after a short delay
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// ─── Helper: translate with chunking + progress ──────────────────────────────

/**
 * Splits `text` into 50 000-char chunks, translates each one,
 * concatenates the results, and reports progress via `onProgress`.
 *
 * @param {string}   text           - Full extracted text
 * @param {Function} translateFn    - Your existing translate function:
 *                                    (chunkText) => Promise<string>
 * @param {Function} onProgress     - Called after each chunk:
 *                                    ({ current, total, percent }) => void
 * @returns {Promise<string>}       - Full translated text
 */
export const translateInChunks = async (text, translateFn, onProgress) => {
  const chunks = splitTextIntoChunks(text, CHUNK_SIZE);
  const total  = chunks.length;
  const translatedChunks = [];

  for (let i = 0; i < chunks.length; i++) {
    const translated = await translateFn(chunks[i]);

    if (translated) {
      translatedChunks.push(translated);
    }

    // Report progress after each completed chunk
    onProgress?.({
      current: i + 1,
      total,
      percent: Math.round(((i + 1) / total) * 100),
    });
  }

  return translatedChunks.join(' ');
};


// // ─── In your component ───────────────────────────────────────────────────────
// //
// // 1. Add progress state near your other useState calls:
// //
// //    const [translationProgress, setTranslationProgress] = useState(null);
// //    // null  = not started
// //    // { current, total, percent } = in progress
// //
// // 2. Replace your existing file loop with this:

// for (const file of files) {
//   // ── Extract text ──────────────────────────────────────────────────────────
//   const text = await extractText(file);

//   if (!text) {
//     setErrorOutputValue(
//       `Extract text from file ${file.name} failed: ` +
//       (extractionError ?? 'Could not extract text from file.')
//     );
//     return;
//   }

//   // ── Translate in 50 000-char chunks ───────────────────────────────────────
//   try {
//     setTranslationProgress({ current: 0, total: 0, percent: 0 }); // show bar

//     const fullTranslation = await translateInChunks(
//       text,

//       // Your existing single-chunk translate call goes here:
//       (chunk) => props.translateText(chunk),

//       // Progress callback — updates the progress bar after each chunk
//       (progress) => setTranslationProgress(progress),
//     );

//     setTranslationProgress(null); // hide bar when done
//     console.log('Full translation:', fullTranslation);

//     // Use fullTranslation however you need (setTranslatedFile, display, etc.)

//   } catch (err) {
//     setTranslationProgress(null);
//     setErrorOutputValue(`Translation failed: ${err.message}`);
//     return;
//   }

//   break;
// }


// ─── Progress bar UI ─────────────────────────────────────────────────────────
//
// Add this anywhere in your JSX return, e.g. below your upload area:
//
//  {translationProgress && (
//    <TranslationProgressBar progress={translationProgress} fileName={file.name} />
//  )}

export const TranslationProgressBar = ({ progress, fileName }) => {
  const { current, total, percent } = progress;

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: 13,
        marginBottom: 6,
        color: '#555',
      }}>
        <span>Translating <strong>{fileName}</strong>… chunk {current} of {total}</span>
        <span>{percent}%</span>
      </div>

      {/* Track */}
      <div style={{
        width: '100%',
        height: 8,
        background: '#e0e0e0',
        borderRadius: 4,
        overflow: 'hidden',
      }}>
        {/* Fill */}
        <div style={{
          width:      `${percent}%`,
          height:     '100%',
          background: '#0070f3',
          borderRadius: 4,
          transition: 'width 0.3s ease',
        }} />
      </div>
    </div>
  );
};